import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';

/**
 * The filtered-total pill that closes every list page's filter row.
 *
 * Each list page already knows its own total — the server returns it as `meta.pagination.total`
 * alongside the page of rows — but until now that number was either buried in a "Records: n" line
 * under the table or not shown at all, so a user changing filters had no quick read on how much
 * the filter actually matched. Same control, same place, same wording on every page, so the
 * number means the same thing wherever it appears.
 *
 * It reports the total for the whole filtered result set, not the rows currently loaded into the
 * table — these pages scroll-load in pages of 15, so binding it to `list.length` would count up
 * as the user scrolls and read as a different (wrong) number.
 */
@Component({
  selector: 'app-record-count',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="record-count">
      <span class="record-count__value">{{ count | number }}</span>
      <span class="record-count__label">{{ noun }}</span>
    </div>
  `,
  styles: [
    `
      :host {
        display: flex;
        align-items: center;
      }

      /**
       * The rule that separates the total from the filters it summarises. It is not a filter and
       * does not behave like one, so it reads as its own thing rather than one more control the
       * user might try to click.
       */
      :host::before {
        content: '';
        width: 1px;
        height: 22px;
        margin: 0 10px;
        background: var(--lightGray);
      }

      /*
       * 34px is the height every other control in these filter rows is pinned to (the outline
       * buttons, ng-select, the search input). The pill has to sit on that same line or it reads
       * as a badge floating over the row rather than the last item in it.
       */
      .record-count {
        display: flex;
        align-items: center;
        gap: 6px;
        height: 34px;
        padding: 0 14px;
        border: 1px solid var(--secondary);
        border-radius: 8px;
        background: var(--secondary);
        white-space: nowrap;
      }

      .record-count__value {
        font-size: 16px;
        font-weight: 700;
        line-height: 1;
        color: var(--black);
      }

      .record-count__label {
        font-size: 12px;
        line-height: 1;
        color: var(--black);
      }

      @media (max-width: 768px) {
        :host::before {
          margin: 0 8px;
        }

        .record-count {
          padding: 0 12px;
        }
      }
    `,
  ],
})
export class RecordCountComponent {
  /**
   * The server's total for the current filter set. Null/undefined while the first request is in
   * flight, which hides the pill rather than flashing a placeholder "0" the user would read as a
   * real (empty) result.
   */
  @Input() count: number | null | undefined = null;

  /** What this page calls its records — "Leads", "Listings", "Tasks". Plural, as it labels a count. */
  @Input() noun = 'Records';

  /**
   * Hidden on the host rather than with an `*ngIf` inside it: the host is a flex item in the
   * filter row, so an emptied-out host would still take the row's `gap` — and its divider — and
   * leave a visible hole between the last filter and the row's edge.
   */
  @HostBinding('class.d-none') get isHidden(): boolean {
    return this.count === null || this.count === undefined;
  }
}
